/**
 * Correct Score / Half Time Correct Score picker styles.
 * Card style shared with Match Details screen (matchDetailsCard.styles.ts).
 */

import { StyleSheet } from 'react-native';
import type { ThemeColors } from '../../../src/theme/themes';
import { getMatchDetailsCardStyle } from './matchDetailsCard.styles';

export const getCorrectScorePickerStyles = (theme: ThemeColors, isDark?: boolean) => {
  const clickableBg = isDark ? theme.background.cardElevated : theme.gray[100];
  const clickableSelectedBg = isDark ? theme.gray[300] : theme.gray[200];
  return StyleSheet.create({
    sectionCard: getMatchDetailsCardStyle(theme),
    sectionTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
      gap: 8,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.gray[900],
      flex: 1,
    },
    sectionCardDeleteButton: {
      padding: 4,
    },
    sectionCardDeleteButtonPressed: { opacity: 0.7 },
    sectionCardDeleteButtonHover: { opacity: 0.85 },
    teamsRow: {
      flexDirection: 'row',
      marginBottom: 8,
    },
    teamLabel: {
      flex: 1,
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray[900],
      textAlign: 'center',
    },
    pickersRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    pickerColumn: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    scoreControlRow: {
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'center',
    },
    scoreControlDeltasRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      marginBottom: 2,
      minHeight: 12,
    },
    deltaCell: {
      width: 28,
      alignItems: 'center',
      justifyContent: 'center',
    },
    deltaSpacer: {
      width: 32,
    },
    scoreControlButtonsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    deltaText: {
      fontSize: 9,
      fontWeight: '700',
    },
    deltaUp: {
      color: theme.error,
    },
    deltaDown: {
      color: theme.success,
    },
    deltaHidden: {
      color: 'transparent',
    },
    /** Odd goes up (higher payout) – subtle green. */
    deltaPositive: {
      color: theme.success,
    },
    /** Odd goes down – neutral gray. */
    deltaNegative: {
      color: theme.gray[600],
    },
    scoreButton: {
      width: 28,
      height: 28,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
    },
    scoreButtonMinus: {
      backgroundColor: clickableSelectedBg,
    },
    iconMinus: {
      color: theme.gray[700],
    },
    scoreButtonPlus: {
      backgroundColor: theme.primary,
    },
    iconPlus: {
      color: theme.white,
    },
    scoreButtonDisabled: {
      opacity: 0.4,
    },
    scoreButtonPressed: { opacity: 0.8 },
    scoreButtonHover: { opacity: 0.9 },
    scoreDisplay: {
      minWidth: 32,
      height: 28,
      backgroundColor: clickableBg,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 4,
    },
    scoreDisplayText: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.gray[900],
    },
    oddsBoxBelow: {
      marginTop: 12,
      alignSelf: 'center',
      minWidth: 80,
      paddingVertical: 10,
      paddingHorizontal: 16,
      backgroundColor: clickableBg,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: isDark ? theme.gray[300] : 'transparent',
    },
    oddsBoxBelowSelected: {
      borderColor: theme.primary,
      backgroundColor: clickableSelectedBg,
    },
    oddsBoxPressed: { opacity: 0.8 },
    oddsBoxHover: { opacity: 0.9 },
    oddsBoxValue: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.accentDark,
    },
    oddsBoxEmpty: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.gray[500],
    },
  });
};
